// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import axios from 'axios';
import { authApi, isNeedsProfile, tokenManager, activeWorkspaceManager } from '../services/api';
import type {
  User,
  BusinessLabel,
  Workspace,
  WorkspaceRole,
  WorkspaceMembership,
  LoginForm,
  SignupCompletePayload,
  OAuthApplePayload,
  OAuthCompletePayload,
  OAuthNeedsProfileResponse,
  ImpersonationContext,
} from '../types';

/**
 * Result of `signInWithGoogle` / `signInWithApple`. Either the user was already
 * a federated customer (`signed_in`) or this is a brand new sign-up — in which
 * case the caller routes them to the inline name + business-name form, holding
 * the `oauthSignupToken` to hand to `completeOAuthSignup`.
 *
 * The two providers share this type so the auth pages can wire both buttons
 * with a single result-handling code path.
 */
export type OAuthSignInResult =
  | { kind: 'signed_in'; user: User }
  | { kind: 'needs_profile'; payload: OAuthNeedsProfileResponse };

interface AuthContextType {
  user: User | null;
  /** Active workspace — the single ambient scope. Null when orphaned. */
  workspace: Workspace | null;
  /** Management role in the active workspace. Null when orphaned. */
  workspaceRole: WorkspaceRole | null;
  /** The active workspace's business labels — filters + explicit write targets.
   *  Never an ambient context: entering a business is navigation, not a switch. */
  organizations: BusinessLabel[];
  /** Every workspace the identity belongs to — drives the switcher. */
  memberships: WorkspaceMembership[];
  /** True when the identity has no active membership anywhere (route to onboarding). */
  isOrphan: boolean;
  /** True when this browser holds Zamino staff access (gates the /admin portal). */
  isPlatformAdmin: boolean;
  /** Set when a platform admin is signed in AS this user — drives the banner.
   *  Server-sourced, so it survives a refresh. */
  impersonation: ImpersonationContext | null;
  /** Switch the active workspace, then refetch. */
  switchWorkspace: (workspaceId: string) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginForm & { totpCode?: string }) => Promise<void>;
  /**
   * Final step of the multi-step sign-up wizard. Creates the org/user and
   * authenticates immediately — 2FA is optional and enrolled later from
   * Settings → Security.
   */
  completeSignup: (data: SignupCompletePayload) => Promise<void>;
  /**
   * Hand a Google ID token to the backend. Either signs the user in (existing
   * federated user) or returns a `needs_profile` payload that the caller uses
   * to drive the inline name + business-name form before calling
   * `completeOAuthSignup`. OAuth users skip MFA entirely.
   */
  signInWithGoogle: (idToken: string) => Promise<OAuthSignInResult>;
  /**
   * Hand an Apple ID token + optional first-sign-in name payload to the backend.
   * Same response semantics as `signInWithGoogle` — either signs in or returns
   * a `needs_profile` payload. OAuth users skip MFA entirely.
   */
  signInWithApple: (payload: OAuthApplePayload) => Promise<OAuthSignInResult>;
  /** Second leg of the OAuth signup — creates the workspace/user and authenticates. */
  completeOAuthSignup: (data: OAuthCompletePayload) => Promise<void>;
  /** Re-fetch the current user (e.g. after enabling/disabling 2FA, or accepting an invite). */
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const queryClient = useQueryClient();

  // Query for current user data
  const {
    data: userData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: authApi.getCurrentUser,
    enabled: !!tokenManager.get(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update auth state when user data changes.
  //
  // Deliberately does NOT clear the token on error. The interceptor owns that
  // decision and only acts on an explicit end-of-session code — clearing here
  // used to turn a refreshable 401, or a transient 503, into a hard sign-out
  // that also made every in-flight request fail with "no token".
  useEffect(() => {
    if (userData?.user) {
      setIsAuthenticated(true);
    } else if (error && !tokenManager.get()) {
      setIsAuthenticated(false);
    }
  }, [userData, error]);

  // The session ended for real (interceptor said so) — drop the app out of the
  // authenticated tree. Navigation is handled by RouteRestorer in App.tsx.
  useEffect(() => {
    const onUnauthorized = () => {
      setIsAuthenticated(false);
      queryClient.clear();
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [queryClient]);

  // Persist whatever workspace the backend actually resolved, so the next request
  // pins it explicitly instead of re-deriving it from last_active. Covers the
  // paths that authenticate without a workspace in the response (sign-up, OAuth
  // sign-up, invite accept) and heals a stale/foreign id left in localStorage.
  useEffect(() => {
    const resolved = userData?.workspace?.id;
    if (resolved) {
      activeWorkspaceManager.set(resolved);
    } else if (userData?.user) {
      activeWorkspaceManager.remove();
    }
  }, [userData]);

  // Check for existing token on mount
  useEffect(() => {
    const token = tokenManager.get();
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // A 403 `workspace_required` means the cached identity is stale (membership
  // revoked elsewhere). Re-fetch it; the resulting orphan state routes to /welcome.
  useEffect(() => {
    const onWorkspaceRequired = () => void refetch();
    window.addEventListener('auth:workspace_required', onWorkspaceRequired);
    return () => window.removeEventListener('auth:workspace_required', onWorkspaceRequired);
  }, [refetch]);

  const login = async (data: LoginForm & { totpCode?: string }) => {
    try {
      const response = await authApi.login(data);

      // MFA required — caller handles this via the thrown response
      if (response.mfaRequired) {
        throw { mfaRequired: true };
      }

      if (!response.token || !response.user) {
        throw new Error('Invalid login response');
      }

      // Persist the active workspace (Layer 4) so the X-Workspace-Id header is
      // set before the first authenticated request. login resumes last_active.
      if (response.workspace?.id) {
        activeWorkspaceManager.set(response.workspace.id);
      }

      // Seed the currentUser cache with the full active-context shape.
      queryClient.setQueryData(['currentUser'], {
        user: response.user,
        workspace: response.workspace ?? null,
        workspaceRole: response.workspaceRole ?? null,
        organizations: response.organizations ?? [],
        memberships: response.memberships ?? [],
      });

      setIsAuthenticated(true);
      toast.success(`Welcome back, ${response.user.firstName}!`);
    } catch (error) {
      // Bare-object sentinel thrown by login() above signals the MFA prompt to the caller.
      if (
        typeof error === 'object' &&
        error !== null &&
        'mfaRequired' in error &&
        (error as { mfaRequired?: boolean }).mfaRequired
      ) {
        throw error;
      }
      let message = 'Login failed';
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as { error?: string; message?: string } | undefined;
        message = data?.error || data?.message || message;
      }
      toast.error(message);
      throw error;
    }
  };

  const completeSignup = async (data: SignupCompletePayload): Promise<void> => {
    // Errors here are surfaced inline by the wizard (silenceToast=true on the request),
    // so we don't toast on failure — we just re-throw and let the caller render.
    const response = await authApi.signupComplete(data);
    if (!response.user) {
      throw new Error('Signup response missing user');
    }
    // The account is active immediately (2FA is optional), so authenticate now.
    setIsAuthenticated(true);
    // Sign-up returns the new business, not the workspace; /me resolves the
    // active workspace from the freshly seeded last_active and the effect above
    // pins it for subsequent requests.
    await refetch();
    toast.success(`Welcome to Zamino, ${response.user.firstName}!`);
  };

  const finaliseOAuthLogin = async (
    response: Awaited<ReturnType<typeof authApi.oauthGoogle>>,
  ): Promise<OAuthSignInResult> => {
    if (isNeedsProfile(response)) {
      return { kind: 'needs_profile', payload: response };
    }
    if (!response.token || !response.user) {
      throw new Error('OAuth response missing user');
    }
    if (response.workspace?.id) {
      activeWorkspaceManager.set(response.workspace.id);
    }
    setIsAuthenticated(true);
    await refetch(); // hydrate full active context (workspace/role/memberships)
    toast.success(`Welcome back, ${response.user.firstName}!`);
    return { kind: 'signed_in', user: response.user };
  };

  const signInWithGoogle = async (idToken: string): Promise<OAuthSignInResult> => {
    // Errors (collision, bad token, server not configured) are silenced at the
    // axios layer so the auth pages can render them inline. We just re-throw.
    const response = await authApi.oauthGoogle({ idToken });
    return finaliseOAuthLogin(response);
  };

  const signInWithApple = async (
    payload: OAuthApplePayload,
  ): Promise<OAuthSignInResult> => {
    const response = await authApi.oauthApple(payload);
    return finaliseOAuthLogin(response);
  };

  const completeOAuthSignup = async (data: OAuthCompletePayload): Promise<void> => {
    const response = await authApi.oauthComplete(data);
    if (!response.user) {
      throw new Error('OAuth signup response missing user');
    }
    // OAuth users skip MFA — flip auth on immediately.
    if (response.workspace?.id) {
      activeWorkspaceManager.set(response.workspace.id);
    }
    setIsAuthenticated(true);
    await refetch(); // hydrate full active context (workspace/role/memberships)
    toast.success(`Welcome to Zamino, ${response.user.firstName}!`);
  };

  /**
   * Re-fetch the current user and ensure auth state is hydrated. Used after
   * accepting an invite (session token just stored) and after enabling/disabling
   * 2FA in settings (to pick up the new mfaEnabled flag).
   */
  const refreshUser = async () => {
    const result = await refetch();
    if (result.data?.user) {
      setIsAuthenticated(true);
    }
  };

  /**
   * Switch the active workspace. Persists the choice (X-Workspace-Id header +
   * backend last_active), drops every cached workspace-scoped query, and
   * refetches the identity in the new context. Wholesale cache clearing is fine
   * here: switching workspaces is rare, unlike entering a business — which is
   * navigation, not a context switch, and never lands here (§10.2).
   */
  const switchWorkspace = async (workspaceId: string) => {
    if (workspaceId === userData?.workspace?.id) return;
    await authApi.switchWorkspace(workspaceId);
    queryClient.clear();
    await refetch();
  };

  const logout = async () => {
    // Flip the UI immediately; the server revocation happens inside
    // authApi.logout() and must not hold up the sign-out the user asked for.
    setIsAuthenticated(false);
    await authApi.logout();
    queryClient.clear();
    toast.success('Signed out');
  };

  const value: AuthContextType = {
    user: userData?.user || null,
    workspace: userData?.workspace || null,
    workspaceRole: userData?.workspaceRole || null,
    organizations: userData?.organizations || [],
    memberships: userData?.memberships || [],
    isOrphan: !!userData?.user && !userData?.workspace,
    // While impersonating, `user` is the customer — who has no platform role —
    // so the portal disappears until the admin exits. That is the intent.
    isPlatformAdmin: !!userData?.user?.platformRole,
    impersonation: userData?.impersonation ?? null,
    switchWorkspace,
    isAuthenticated,
    isLoading,
    login,
    completeSignup,
    signInWithGoogle,
    signInWithApple,
    completeOAuthSignup,
    refreshUser,
    logout,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Permission hook — resolves from the ACTIVE workspace management role
 * (Workspaces Layer 3). Mirrors the backend split (WORKSPACE_ROLE_PERMISSIONS):
 * every member can do content / analysis / consumption; only owner + admin get
 * administration (billing, users, sources, org settings, access grants).
 *
 * Data *visibility* is never a role here — it's a per-source grant enforced by
 * the backend. These flags gate management UI + the actions members can take.
 */
export const usePermissions = () => {
  const { workspaceRole } = useAuth();

  const isOwner = workspaceRole === 'workspace-owner';
  const isAdmin = workspaceRole === 'workspace-owner' || workspaceRole === 'workspace-admin';
  const isMember = !!workspaceRole; // any active membership

  // Administration — owner/admin only.
  const canManageOrganization = () => isAdmin;
  const canManageUsers = () => isAdmin;
  const canManageAccounts = () => isAdmin; // connecting a source grows the billing pool
  const canManageBilling = () => isAdmin;

  // Content / analysis / consumption — every member.
  const canReviewTransactions = () => isMember;
  const canExportData = () => isMember;
  const canManageAnalysis = () => isMember;
  const canInitiateChat = () => isMember;

  return {
    workspaceRole,
    canManageOrganization,
    canManageUsers,
    canManageAccounts,
    canManageBilling,
    canReviewTransactions,
    canExportData,
    canManageAnalysis,
    canInitiateChat,
    isOwner,
    isAdmin,
    isMember,
  };
};
